import React, {useCallback, useEffect, useState} from "react";
import {
  Box, Button, CircularProgress, Collapse, Fade, IconButton, InputBase, MenuItem, Modal, Select, Stack, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  Add01Icon, ArrowDown01Icon, ArrowUp01Icon, Cancel01Icon, Delete02Icon, PencilEdit01Icon, UserGroup03Icon
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import useConfirm from "../../../hooks/useConfirm";
import {groupCount as countOf, WebrtcRepository, webrtcErrorCode, webrtcErrorMessage} from "../../../repositories/WebrtcRepository";
import {subProjectLabel} from "../../../utils/subProjects";
import {useRegistryProject} from "../RegistryProjectContext";
import {controlSx, labelSx, selectMenuProps, SectionHeader} from "./settingsStyles";

const DETAIL_POLL_MS = 5000;
const DEFAULTS = {wait_timeout: 60, ring_timeout: 20};
const LIMITS = {wait_timeout: [5, 3600], ring_timeout: [5, 300]};

const primaryButtonSx = {
  textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px",
  bgcolor: "rgba(132, 145, 247, 0.12)", color: "#4B55C9", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.2)"},
  "&.Mui-disabled": {bgcolor: "#F0F0F6", color: "#B4B4C3"},
};

const ghostButtonSx = {
  textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px",
  color: "#374151", bgcolor: "transparent", "&:hover": {bgcolor: "#ECECF3"},
};

const iconButtonSx = {width: 32, height: 32, borderRadius: "9px", color: "#6B6B80", "&:hover": {bgcolor: "#ECECF3"}};

const errorOf = (e, fallback) => webrtcErrorCode(e) === "number_taken"
  ? "Number already used by another group"
  : webrtcErrorMessage(e, fallback);

const formatWait = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
};

// Agent groups of the project (ADMIN / MANAGER): a number that rings every joined agent's browser at once. Calls
// nobody answers within wait_timeout (or with nobody online) go to the group's transfer number.
export const RegistryAgentGroupsSettings = () => {
  const {project} = useRegistryProject();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null: closed, {}: new group, group: edit
  const [expanded, setExpanded] = useState(null);
  const confirmDelete = useConfirm("Delete this group?", "Calls sent to its number will no longer reach the agents.");

  const load = useCallback(() => WebrtcRepository.getGroups({projectId: project.id})
    .then(({data}) => setGroups(data?.groups || []))
    .catch(e => toast.error(webrtcErrorMessage(e, "Failed to load the agent groups")))
    .finally(() => setLoading(false)), [project.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (group) => {
    try {
      if (editing?.id) {
        await WebrtcRepository.editGroup({projectId: project.id, groupId: editing.id, group});
        toast.success(`Updated "${group.name}"`);
      } else {
        const {data} = await WebrtcRepository.createGroup({projectId: project.id, group});
        toast.success(`Created "${group.name}"${data?.number ? ` with number ${data.number}` : ""}`);
      }
      await load();
      return true;
    } catch (e) {
      toast.error(errorOf(e, "Failed to save the group"));
      return false;
    }
  };

  const handleDelete = async (group) => {
    if (!(await confirmDelete({title: `Delete "${group.name}"?`}))) return;
    try {
      await WebrtcRepository.deleteGroup({projectId: project.id, groupId: group.id});
      toast.success(`Deleted "${group.name}"`);
      if (expanded === group.id) setExpanded(null);
      await load();
    } catch (e) {
      toast.error(webrtcErrorMessage(e, "Failed to delete the group"));
    }
  };

  return (
    <>
      <GroupDialog group={editing} onClose={() => setEditing(null)} onSubmit={handleSave}/>
      <Box sx={{px: {xs: 2, md: 4}, py: {xs: 2, md: 3}}}>
        <SectionHeader
          icon={<HugeiconsIcon icon={UserGroup03Icon} size={18}/>}
          title={"Agent Groups"}
          description={"A group number rings every joined agent's browser at once. Unanswered calls go to its transfer number."}
          action={
            <Button
              onClick={() => setEditing({})}
              startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
              sx={{...primaryButtonSx, width: {xs: "100%", sm: "auto"}}}
            >
              Add Group
            </Button>
          }
        />

        <Stack gap={1.25}>
          {loading && <Box sx={{display: "flex", justifyContent: "center", py: 3}}><CircularProgress size={22}/></Box>}
          {!loading && groups.length === 0 && (
            <Typography sx={{fontSize: 13, color: "#8A8AA0", py: 2, textAlign: "center"}}>
              No agent groups yet. Add a group, then agents join it from the browser phone.
            </Typography>
          )}
          {groups.map(group => (
            <GroupRow
              key={group.id}
              group={group}
              projectId={project.id}
              expanded={expanded === group.id}
              onToggle={() => setExpanded(current => current === group.id ? null : group.id)}
              onEdit={() => setEditing(group)}
              onDelete={() => handleDelete(group)}
            />
          ))}
        </Stack>
      </Box>
    </>
  );
};

const Stat = ({label, value, accent}) => (
  <Box sx={{minWidth: 56}}>
    <Typography sx={{fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", color: "#8A8AA0", textTransform: "uppercase"}}>
      {label}
    </Typography>
    <Typography sx={{fontSize: 14, fontWeight: 700, color: accent && value ? "#C7781E" : "#23264D"}}>{countOf(value)}</Typography>
  </Box>
);

const GroupRow = ({group, projectId, expanded, onToggle, onEdit, onDelete}) => (
  <Box sx={{borderRadius: "12px", bgcolor: "#FAFAFD"}}>
    <Stack direction={{xs: "column", sm: "row"}} alignItems={{xs: "stretch", sm: "center"}} gap={1.5} sx={{p: 1.5}}>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography noWrap sx={{fontSize: 14, fontWeight: 700, color: "#23264D"}}>{group.name}</Typography>
        <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>
          Number <Box component={"span"} sx={{fontFamily: "monospace", color: "#4A4A63"}}>{group.number || "–"}</Box>
          {" · "}Transfer to <Box component={"span"} sx={{fontFamily: "monospace", color: "#4A4A63"}}>{group.transfer_number || "–"}</Box>
        </Typography>
      </Box>
      <Stack direction={"row"} gap={2} alignItems={"center"}>
        <Stat label={"Members"} value={group.members_count}/>
        <Stat label={"Online"} value={group.online_count}/>
        <Stat label={"Waiting"} value={group.waiting_count} accent/>
        <Stack direction={"row"} gap={0.25}>
          <Tooltip title={expanded ? "Hide details" : "Members and waiting callers"}>
            <IconButton onClick={onToggle} sx={iconButtonSx} aria-label={"Details"}>
              <HugeiconsIcon icon={expanded ? ArrowUp01Icon : ArrowDown01Icon} size={16}/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Edit"}>
            <IconButton onClick={onEdit} sx={iconButtonSx} aria-label={"Edit"}>
              <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Delete"}>
            <IconButton onClick={onDelete} sx={{...iconButtonSx, color: "#C73A3A", "&:hover": {bgcolor: "#FBECEC"}}} aria-label={"Delete"}>
              <HugeiconsIcon icon={Delete02Icon} size={16}/>
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
    <Collapse in={expanded} unmountOnExit>
      <GroupDetail projectId={projectId} groupId={group.id}/>
    </Collapse>
  </Box>
);

// Members (online / available) and the callers waiting right now, refreshed while open
const GroupDetail = ({projectId, groupId}) => {
  const {basePath} = useRegistryProject();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchDetail = () => WebrtcRepository.getGroup({projectId, groupId})
      .then(({data}) => alive && setDetail(data))
      .catch(e => console.error("Failed to load the group", e));
    fetchDetail();
    const id = setInterval(fetchDetail, DETAIL_POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [projectId, groupId]);

  if (!detail) return <Box sx={{display: "flex", justifyContent: "center", pb: 2}}><CircularProgress size={18}/></Box>;
  const members = detail.members || [];
  const waiting = detail.waiting || [];

  return (
    <Stack direction={{xs: "column", md: "row"}} gap={2} sx={{px: 1.5, pb: 1.5}}>
      <Box sx={{flex: 1}}>
        <Typography sx={labelSx}>Members</Typography>
        {members.length === 0 && <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>Nobody joined yet</Typography>}
        {members.map((member, index) => (
          <Stack key={index} direction={"row"} alignItems={"center"} gap={1} sx={{py: 0.5}}>
            <Box sx={{
              width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
              bgcolor: member.available ? "#22C55E" : member.online ? "#F59E0B" : "#C9CAD8",
            }}/>
            <Typography noWrap sx={{fontSize: 13, color: "#23264D", flex: 1}}>{member.display_name}</Typography>
            <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
              {member.available ? "Available" : member.online ? (member.state || "Busy") : "Offline"}
            </Typography>
          </Stack>
        ))}
      </Box>
      <Box sx={{flex: 1}}>
        <Typography sx={labelSx}>Waiting callers</Typography>
        {waiting.length === 0 && <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>Nobody is waiting</Typography>}
        {waiting.map(caller => (
          <Stack key={`${caller.position}-${caller.caller_id}`} direction={"row"} alignItems={"center"} gap={1} sx={{py: 0.5}}>
            <Typography sx={{fontSize: 12, fontWeight: 700, color: "#8491F7", width: 20}}>#{caller.position}</Typography>
            <Typography noWrap sx={{fontSize: 13, color: "#23264D", flex: 1}}>{caller.caller_id || "Unknown"}</Typography>
            <Typography sx={{fontSize: 12, color: "#C7781E"}}>{formatWait(caller.wait_seconds)}</Typography>
            {caller.conversation_id && (
              <Button
                size={"small"}
                onClick={() => navigate(`${basePath}/supervision?conversation=${encodeURIComponent(caller.conversation_id)}`)}
                sx={{...ghostButtonSx, fontSize: 12, minWidth: 0, px: 1, py: 0.25, color: "#4B55C9"}}
              >
                Open
              </Button>
            )}
          </Stack>
        ))}
      </Box>
    </Stack>
  );
};

const Field = ({label, hint, children}) => (
  <Box>
    <Typography sx={labelSx}>{label}</Typography>
    {children}
    {hint && <Typography sx={{fontSize: 11, color: "#8A8AA0", mt: 0.75}}>{hint}</Typography>}
  </Box>
);

const TextControl = ({value, onChange, placeholder, type = "text", inputProps, autoFocus}) => (
  <Box sx={controlSx}>
    <InputBase
      value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} fullWidth
      autoFocus={autoFocus} inputProps={inputProps} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
    />
  </Box>
);

const emptyForm = (group) => ({
  name: group?.name || "",
  number: group?.number || "",
  transfer_number: group?.transfer_number || "",
  sub_project_id: group?.sub_project_id ? String(group.sub_project_id) : "",
  wait_timeout: String(group?.wait_timeout ?? DEFAULTS.wait_timeout),
  ring_timeout: String(group?.ring_timeout ?? DEFAULTS.ring_timeout),
});

// Create (group = {}) or edit a group. Empty number: assigned automatically (7000000+).
const GroupDialog = ({group, onClose, onSubmit}) => {
  const {project} = useRegistryProject();
  const subProjects = project?.subProjects || [];
  const open = Boolean(group);
  const isEdit = Boolean(group?.id);
  const [form, setForm] = useState(emptyForm(null));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(emptyForm(group));
  }, [open, group]);

  const set = (key) => (value) => setForm(current => ({...current, [key]: value}));
  const outOfRange = (key) => {
    const value = Number(form[key]);
    const [min, max] = LIMITS[key];
    return !Number.isInteger(value) || value < min || value > max;
  };
  const invalid = !form.name.trim() || outOfRange("wait_timeout") || outOfRange("ring_timeout");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (invalid) return;
    const body = {
      name: form.name.trim(),
      transfer_number: form.transfer_number.trim(),
      // cleared on edit, left out on create
      sub_project_id: form.sub_project_id || (isEdit ? null : undefined),
      wait_timeout: Number(form.wait_timeout),
      ring_timeout: Number(form.ring_timeout),
    };
    // an empty number is assigned by the server on create, and kept as it is on edit
    if (form.number.trim()) body.number = form.number.trim();
    setSaving(true);
    const success = await onSubmit(body);
    setSaving(false);
    if (success) onClose();
  };

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Fade in={open}>
        <Box
          component={"form"}
          onSubmit={handleSubmit}
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: {xs: "calc(100% - 24px)", md: "min(480px, 92vw)"}, maxHeight: "calc(100vh - 32px)", overflowY: "auto",
            bgcolor: "#fff", borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)", p: {xs: 2.5, md: 3},
            outline: "none",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2.5}}>
            <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
              {isEdit ? "Edit Agent Group" : "Add Agent Group"}
            </Typography>
            <IconButton size="small" onClick={onClose} disabled={saving} sx={{color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Stack gap={2} sx={{mb: 2.5}}>
            <Field label={"Name"}>
              <TextControl value={form.name} onChange={set("name")} placeholder={"Sales"} autoFocus/>
            </Field>
            <Field label={"Number"} hint={isEdit
              ? "The number calls are transferred to."
              : "Optional. Leave empty to assign one automatically (7000000+), or enter an existing VDN number."}>
              <TextControl value={form.number} onChange={set("number")} placeholder={"Automatic"} inputProps={{inputMode: "tel"}}/>
            </Field>
            <Field label={"Transfer number"} hint={"Where calls go when nobody answers or nobody is online: a phone number, an agent number or another group's number."}>
              <TextControl value={form.transfer_number} onChange={set("transfer_number")} placeholder={"+389 2 123 456"} inputProps={{inputMode: "tel"}}/>
            </Field>
            {subProjects.length > 0 && (
              <Field label={"Sub project"} hint={"Optional, a label only: used to filter and show groups."}>
                <Select
                  variant={"standard"} disableUnderline fullWidth displayEmpty value={form.sub_project_id}
                  onChange={e => set("sub_project_id")(e.target.value)} MenuProps={selectMenuProps}
                  sx={{...controlSx, "& .MuiSelect-select": {py: 0}}}
                >
                  <MenuItem value={""} sx={{fontSize: 13}}>None</MenuItem>
                  {subProjects.map(item => (
                    <MenuItem key={item.id} value={String(item.id)} sx={{fontSize: 13}}>{subProjectLabel(item)}</MenuItem>
                  ))}
                </Select>
              </Field>
            )}
            <Stack direction={{xs: "column", sm: "row"}} gap={2}>
              <Box sx={{flex: 1}}>
                <Field label={"Wait timeout (s)"} hint={outOfRange("wait_timeout")
                  ? "Between 5 and 3600 seconds"
                  : "How long a caller waits before going to the transfer number."}>
                  <TextControl value={form.wait_timeout} onChange={set("wait_timeout")} type={"number"} inputProps={{min: 5, max: 3600}}/>
                </Field>
              </Box>
              <Box sx={{flex: 1}}>
                <Field label={"Ring timeout (s)"} hint={outOfRange("ring_timeout")
                  ? "Between 5 and 300 seconds"
                  : "How long one ring round lasts."}>
                  <TextControl value={form.ring_timeout} onChange={set("ring_timeout")} type={"number"} inputProps={{min: 5, max: 300}}/>
                </Field>
              </Box>
            </Stack>
          </Stack>

          <Stack direction="row" gap={1.5} justifyContent="flex-end">
            <Button type={"button"} onClick={onClose} disabled={saving} sx={ghostButtonSx}>Cancel</Button>
            <Button
              type={"submit"} disabled={saving || invalid}
              startIcon={saving ? <CircularProgress size={14} sx={{color: "#4B55C9"}}/> : null}
              sx={primaryButtonSx}
            >
              {isEdit ? "Save" : "Add Group"}
            </Button>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};
